"use client"

import { nanoid } from "nanoid"
import { useEffect, useRef } from "react"
import { ResolvedListProductData } from "src/data/product-list-resolved/types"
import { WCStoreCreateOrderResponseData } from "src/data/woocommerce/wcStoreTypes"
import { useInViewEffectOncePerPage } from "src/hooks/useInViewEffectOncePerPage"
import { useWindowLocationPathname } from "src/hooks/useWindowLocationPathname"
import {
  googleTrackAddPaymentInfo,
  googleTrackAddShippingInfo,
  googleTrackAddToCart,
  googleTrackBeginCheckout,
  googleTrackEmbedView,
  googleTrackPageView,
  googleTrackPurchase,
  googleTrackRemoveFromCart,
  googleTrackSearch,
  googleTrackSelectItem,
  googleTrackSelectPromotion,
  googleTrackViewCart,
  googleTrackViewItem,
  googleTrackViewItemList,
  googleTrackViewPromotion,
} from "src/integrations/google/api"
import {
  mapCartOrderToGoogleAddPaymentInfoEvent,
  mapCartToGoogleAddShippingInfoEvent,
  mapCartToGoogleBeginCheckoutEvent,
  mapCartToGoogleViewCartEvent,
  mapLineItemToGoogleAddToCartEvent,
  mapOrderToGooglePurchaseEvent,
  mapProductToGoogleItemData,
  mapProductToGoogleRemoveFromCartEvent,
  mapProductToGoogleViewItemEvent,
  mapProductToGoogleViewPromotionItemData,
} from "src/integrations/google/mapping"
import {
  klaviyoTrackAddedToCart,
  klaviyoTrackStartedCheckout,
  klaviyoTrackViewedProduct,
} from "src/integrations/klaviyo/events"
import {
  mapCartChangeToKlaviyoAddedToCartEvent,
  mapCartToKlaviyoStartedCheckoutEvent,
  mapProductToKlaviyoViewedProductEvent,
} from "src/integrations/klaviyo/mapping"
import {
  metaTrackAddPaymentInfo,
  metaTrackAddToCart,
  metaTrackInitiateCheckout,
  metaTrackPageView,
  metaTrackPurchase,
  metaTrackSearch,
  metaTrackViewContent,
} from "src/integrations/meta/events"
import {
  mapCartOrderToMetaAddPaymentInfoEvent,
  mapCartToMetaInitiateCheckoutEvent,
  mapLineItemToMetaAddToCartEvent,
  mapOrderToMetaPurchaseEvent,
  mapProductToMetaViewContentEvent,
} from "src/integrations/meta/mapping"
import {
  posthogTrackCheckoutStepViewed,
  posthogTrackCouponApplied,
  posthogTrackEmailSignup,
  posthogTrackOrderCompleted,
  posthogTrackPageView,
  posthogTrackProductAdded,
  posthogTrackProductsSearched,
} from "src/integrations/posthog/events"
import {
  mapCartChangeToTrackingLineItem,
  mapCartToTrackingCart,
  mapListProductToTrackingProduct,
  mapOrderToTrackingOrder,
  mapResolvedListProductToTrackingProduct,
} from "src/tracking/mapping"
import { StorefrontCart, StorefrontCartItem, StorefrontOrder } from "src/types/storefront"
import { createCartHash } from "src/utilities/cart"
import { isBrowser } from "src/utilities/env"
import { useCartState } from "../checkout/hooks/useCart"
import { getOrder } from "../checkout/state/checkoutOrderStore"
import { CheckoutStep, useCheckoutStep } from "../checkout/state/checkoutStageStore"
import { ListProductData } from "../data/product-list/types"

export function generateTrackingEventId() {
  return nanoid()
}

function trackPageView({ referrer, pathname }: { referrer: string; pathname: string }) {
  const event_id = generateTrackingEventId()

  googleTrackPageView({ event_id, referrer })
  metaTrackPageView({ event_id })
  posthogTrackPageView({ referrer, pathname })
}

// Use a non-hook variable to avoid duplicate page view tracking caused by local
// React Strict Mode.
let previousPathname = ""
let previousHref = ""

export function useTrackPageViews() {
  const pathname = useWindowLocationPathname()

  if (!isBrowser) {
    return
  }

  // ! Track page views outside of an effect so they are tracked before anything
  // else that could be called in an effect.
  if (!previousPathname) {
    const referrer = document.referrer

    trackPageView({ referrer, pathname })
  } else if (pathname !== previousPathname) {
    const referrer = previousHref

    trackPageView({ referrer, pathname })
  }

  previousPathname = pathname
  previousHref = window.location.href
}

export function useTrackViewItemList(listId: string, products: ListProductData[]) {
  return useInViewEffectOncePerPage({
    id: `useTrackViewItemList | ${listId}`,
    effect: () => {
      const trackingProducts = products.map(mapListProductToTrackingProduct)
      const event_id = generateTrackingEventId()

      googleTrackViewItemList({
        event_id,
        item_list_id: listId,
        items: trackingProducts.map(mapProductToGoogleItemData),
      })
    },
  })
}

export function trackSearch({ listId, input }: { listId: string; input: string }) {
  const event_id = generateTrackingEventId()

  const inputCleaned = input.trim().toLowerCase()

  if (!inputCleaned) {
    return
  }

  googleTrackSearch({ event_id, search_term: inputCleaned })
  metaTrackSearch({ event_id, search_string: inputCleaned })
  posthogTrackProductsSearched({ listId, input: inputCleaned })
}

export function trackProductLinkClick(listId: string, product: ListProductData) {
  const trackingProduct = mapListProductToTrackingProduct(product)
  const item = mapProductToGoogleItemData(trackingProduct)
  const event_id = generateTrackingEventId()

  googleTrackSelectItem({ event_id, item_list_id: listId, items: [item] })

  if (trackingProduct.promotionId) {
    const promotionItem = mapProductToGoogleViewPromotionItemData(trackingProduct)

    googleTrackSelectPromotion({ event_id, items: [promotionItem] })
  }
}

export function trackViewEmbed(data: { integrationId: string; embedId: string }) {
  const event_id = generateTrackingEventId()

  googleTrackEmbedView({ event_id, ...data })
}

export function trackEmailSignup(data: { listId: string; name: string }) {
  posthogTrackEmailSignup(data)
}

function trackViewPromotion(product: ListProductData) {
  const trackingProduct = mapListProductToTrackingProduct(product)

  if (trackingProduct.promotionId) {
    const promotionItem = mapProductToGoogleViewPromotionItemData(trackingProduct)
    const event_id = generateTrackingEventId()

    googleTrackViewPromotion({ event_id, items: [promotionItem] })
  }
}

export function useTrackViewPromotion(product: ListProductData) {
  return useInViewEffectOncePerPage({
    id: `useTrackViewPromotion | ${product.id}`,
    effect: () => trackViewPromotion(product),
  })
}

function trackViewProduct(product: ResolvedListProductData) {
  const trackingProduct = mapResolvedListProductToTrackingProduct(product)
  const event_id = generateTrackingEventId()

  googleTrackViewItem(mapProductToGoogleViewItemEvent(event_id, trackingProduct))
  metaTrackViewContent(mapProductToMetaViewContentEvent(event_id, trackingProduct))
  klaviyoTrackViewedProduct(mapProductToKlaviyoViewedProductEvent(trackingProduct))

  if (trackingProduct.promotionId) {
    const promotionItem = mapProductToGoogleViewPromotionItemData(trackingProduct)

    googleTrackViewPromotion({ event_id, items: [promotionItem] })
  }
}

export function useTrackViewProduct(product: ResolvedListProductData) {
  return useInViewEffectOncePerPage({
    id: `useTrackViewProduct | ${product.id}`,
    effect: () => trackViewProduct(product),
  })
}

export function trackAddToCart(cart: StorefrontCart, item: StorefrontCartItem, quantityChange: number) {
  const trackingCart = mapCartToTrackingCart(cart)
  const trackingLineItem = mapCartChangeToTrackingLineItem(cart, item, quantityChange)
  const event_id = generateTrackingEventId()

  googleTrackAddToCart(mapLineItemToGoogleAddToCartEvent(event_id, trackingLineItem))
  metaTrackAddToCart(mapLineItemToMetaAddToCartEvent(event_id, trackingLineItem))
  klaviyoTrackAddedToCart(mapCartChangeToKlaviyoAddedToCartEvent(trackingCart, trackingLineItem, quantityChange))
  posthogTrackProductAdded(trackingLineItem)
}

export function trackRemoveFromToCart(cart: StorefrontCart, item: StorefrontCartItem, quantityChange: number) {
  const trackingCart = mapCartToTrackingCart(cart)
  const trackingLineItem = mapCartChangeToTrackingLineItem(cart, item, quantityChange)
  const event_id = generateTrackingEventId()

  googleTrackRemoveFromCart(mapProductToGoogleRemoveFromCartEvent(event_id, trackingLineItem))
  klaviyoTrackAddedToCart(mapCartChangeToKlaviyoAddedToCartEvent(trackingCart, trackingLineItem, -quantityChange))
}

function trackViewCart(cart: StorefrontCart) {
  const trackingCart = mapCartToTrackingCart(cart)
  const event_id = generateTrackingEventId()

  googleTrackViewCart(mapCartToGoogleViewCartEvent(event_id, trackingCart))
}

export function trackCheckoutStep(step: CheckoutStep) {
  posthogTrackCheckoutStepViewed(step)
}

export function useTrackViewCart() {
  const step = useCheckoutStep()
  const cart = useCartState().data
  const cartHashRef = useRef("")

  const cartHash = createCartHash(cart)

  useEffect(() => {
    if (!cart || step !== CheckoutStep.Cart || cartHash === cartHashRef.current) {
      return
    }

    trackViewCart(cart)

    cartHashRef.current = cartHash
  }, [step, cart, cartHash])
}

export function trackCouponApplied(code: string) {
  const codeLower = code.toLowerCase()

  posthogTrackCouponApplied({ code: codeLower })
}

export function trackBeginCheckout(cart: StorefrontCart) {
  const trackingCart = mapCartToTrackingCart(cart)
  const event_id = generateTrackingEventId()

  googleTrackBeginCheckout(mapCartToGoogleBeginCheckoutEvent(event_id, trackingCart))
  metaTrackInitiateCheckout(mapCartToMetaInitiateCheckoutEvent(event_id, trackingCart))
  klaviyoTrackStartedCheckout(mapCartToKlaviyoStartedCheckoutEvent(trackingCart))
}

export function trackAddShippingInfo(cart: StorefrontCart) {
  const trackingCart = mapCartToTrackingCart(cart)
  const event_id = generateTrackingEventId()

  googleTrackAddShippingInfo(mapCartToGoogleAddShippingInfoEvent(event_id, trackingCart))
}

export function trackAddPaymentInfo(cart: StorefrontCart, order: WCStoreCreateOrderResponseData) {
  const trackingCart = mapCartToTrackingCart(cart, order.payment_method)
  const event_id = generateTrackingEventId()

  googleTrackAddPaymentInfo(mapCartOrderToGoogleAddPaymentInfoEvent(event_id, trackingCart))
  metaTrackAddPaymentInfo(mapCartOrderToMetaAddPaymentInfoEvent(event_id, trackingCart))
}

export function isOrderSucecssful(order: StorefrontOrder | undefined) {
  if (!order) {
    return false
  }

  const status = order.status

  return status === "completed" || status === "processing" || status === "pending" || status === "on-hold"
}

export function trackPurchase() {
  const order = getOrder()

  if (!order || !isOrderSucecssful(order)) {
    return
  }

  const trackingOrder = mapOrderToTrackingOrder(order)
  // Here, the event_id is the order ID.

  googleTrackPurchase(mapOrderToGooglePurchaseEvent(trackingOrder))
  metaTrackPurchase(mapOrderToMetaPurchaseEvent(trackingOrder))
  posthogTrackOrderCompleted(trackingOrder)
}
